'use client';

import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { MONO_FONT } from '@/theme';

export default function PageSection({
  id,
  dark = false,
  compact = false,
  eyebrow,
  title,
  lede,
  children,
  sx = {},
  ...props
}) {
  return (
    <Box
      component="section"
      id={id}
      data-nav-theme={dark ? 'dark' : 'light'}
      sx={{
        py: compact ? { xs: 7, md: 10 } : { xs: 8, md: 15 },
        ...(dark && { borderTop: '1px solid', borderColor: 'grey.800' }),
        bgcolor: dark ? 'grey.950' : 'transparent',
        color: dark ? 'grey.300' : 'inherit',
        ...(dark && {
          'h1,h2,h3,h4': { color: 'common.white' },
        }),
        ...sx,
      }}
      {...props}
    >
      <Box
        sx={{
          maxWidth: 1240,
          mx: 'auto',
          px: { xs: '20px', sm: '32px' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {(eyebrow || title || lede) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.5fr 1fr' },
              gap: { xs: 2.5, md: 6 },
              mb: { xs: 5, md: 8 },
            }}
          >
            {(eyebrow || title) && (
              <Box>
                {eyebrow && (
                  <Box
                    component="span"
                    sx={{
                      fontFamily: MONO_FONT,
                      fontSize: '11px',
                      fontWeight: 600,
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                      color: 'text.accent',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '12px',
                      mb: 3,
                      '&::before': {
                        content: '""',
                        width: '24px',
                        height: '1px',
                        background: 'currentColor',
                        flexShrink: 0,
                      },
                    }}
                  >
                    <T>{eyebrow}</T>
                  </Box>
                )}
                {title && (
                  <Typography
                    variant="h2"
                    component="h2"
                    sx={{
                      color: dark ? 'common.white' : 'text.primary',
                      // Desde lg, el título ocupa como máximo la mitad del ancho del contenido (el contenedor
                      // mide 1240 px menos el padding de 32 px por lado), y las líneas se reparten parejas.
                      // Así el salto de línea no depende de un <br /> escrito para el español: sirve en
                      // cualquier idioma.
                      maxWidth: { lg: 'calc(min(100vw, 1240px) / 2 - 32px)' },
                      textWrap: 'balance',
                    }}
                  >
                    <T>{title}</T>
                  </Typography>
                )}
              </Box>
            )}
            {lede && (
              <Typography
                sx={{
                  fontSize: 'clamp(14px, 1.5vw, 17px)',
                  lineHeight: 1.6,
                  color: dark ? 'text.muted' : 'grey.600',
                  alignSelf: 'end',
                }}
              >
                <T>{lede}</T>
              </Typography>
            )}
          </Box>
        )}
        <T>{children}</T>
      </Box>
    </Box>
  );
}
